import { initAds } from "./ads/ads";
import {
  BARK_IDS,
  BARKS,
  DOG_NAMES,
  nextCycledName,
  randomName,
} from "@dogdell/shared";
import type { User } from "firebase/auth";
import { DOG_BREEDS, DOG_WALK_FRAMES } from "./game/DogBreeds";
import {
  loadBreedSheetBundle,
  renderInGameDogCanvas,
  type BreedSheetBundle,
} from "./game/WorldArt";
import { ParkView } from "./game/ParkView";
import {
  accountDogName,
  completeMagicLinkSignInIfPresent,
  currentIdToken,
  isFirebaseConfigured,
  sendMagicLink,
  signInWithGoogle,
  signOutAccount,
  watchAuth,
} from "./firebase/auth";
import { NetworkClient } from "./net/NetworkClient";
import { Hud } from "./ui/Hud";
import type { ServerMessage } from "@dogdell/shared";

const LEGAL_STORAGE_KEY = "dogdell-legal-v1";
const NAME_CYCLE_STORAGE_KEY = "dogdell-name-cycle-v1";
const BREED_STORAGE_KEY = "dogdell-breed-v1";

const net = new NetworkClient();
const hud = new Hud((message) => net.send(message));

const gate = document.querySelector("#gate") as HTMLElement;
const gateTitle = document.querySelector(".gate-title") as HTMLElement;
const joinForm = document.querySelector("#join-form") as HTMLFormElement;
const nameInput = document.querySelector("#name-input") as HTMLSelectElement;
const guestEmailInput = document.querySelector("#guest-email-input") as HTMLInputElement;
const shuffleName = document.querySelector("#shuffle-name") as HTMLButtonElement;
const breedCarousel = document.querySelector("#breed-carousel") as HTMLElement | null;
const breedPreview = document.querySelector("#breed-preview") as HTMLCanvasElement | null;
const breedPeekPrev = document.querySelector("#breed-peek-prev") as HTMLCanvasElement | null;
const breedPeekNext = document.querySelector("#breed-peek-next") as HTMLCanvasElement | null;
const breedSlotFocus = document.querySelector("#breed-slot-focus") as HTMLElement | null;
const breedSlotPrev = document.querySelector("#breed-slot-prev") as HTMLButtonElement | null;
const breedSlotNext = document.querySelector("#breed-slot-next") as HTMLButtonElement | null;
const breedNameLabel = document.querySelector("#breed-name-label") as HTMLElement | null;
const breedPrev = document.querySelector("#breed-prev") as HTMLButtonElement | null;
const breedNext = document.querySelector("#breed-next") as HTMLButtonElement | null;
const barkBar = document.querySelector("#bark-bar") as HTMLElement;
const acceptLegalGuest = document.querySelector("#accept-legal-guest") as HTMLInputElement;
const acceptLegalAccount = document.querySelector("#accept-legal-account") as HTMLInputElement;
const tabGuest = document.querySelector("#tab-guest") as HTMLButtonElement;
const tabAccount = document.querySelector("#tab-account") as HTMLButtonElement;
const panelAccount = document.querySelector("#panel-account") as HTMLElement;
const firebaseMissing = document.querySelector("#firebase-missing") as HTMLElement;
const accountSignedOut = document.querySelector("#account-signed-out") as HTMLElement;
const accountSignedIn = document.querySelector("#account-signed-in") as HTMLElement;
const accountSummary = document.querySelector("#account-summary") as HTMLElement;
const googleSignIn = document.querySelector("#google-sign-in") as HTMLButtonElement;
const magicLinkForm = document.querySelector("#magic-link-form") as HTMLFormElement;
const loginEmailInput = document.querySelector("#login-email-input") as HTMLInputElement;
const sendMagicLinkBtn = document.querySelector("#send-magic-link") as HTMLButtonElement;
const magicLinkSent = document.querySelector("#magic-link-sent") as HTMLElement;
const enterAsAccount = document.querySelector("#enter-as-account") as HTMLButtonElement;
const guestEnter = document.querySelector("#guest-enter") as HTMLButtonElement;
const guestEnterBusy = document.querySelector("#guest-enter-busy") as HTMLElement;
const accountEnterBusy = document.querySelector("#account-enter-busy") as HTMLElement;
const guestEnterError = document.querySelector("#guest-enter-error") as HTMLElement;
const accountEnterError = document.querySelector("#account-enter-error") as HTMLElement;
const signOutBtn = document.querySelector("#sign-out") as HTMLButtonElement;

let parkView: ParkView | null = null;
let playing = false;
let authUser: User | null = null;
let selectedBreed = loadSelectedBreed();

function loadSelectedBreed(): number {
  try {
    const raw = localStorage.getItem(BREED_STORAGE_KEY);
    const n = raw == null ? NaN : Number(raw);
    if (Number.isInteger(n) && n >= 0 && n < DOG_BREEDS.length) {
      return n;
    }
  } catch {
    // ignore
  }
  return Math.floor(Math.random() * DOG_BREEDS.length);
}

/** Calm stroll for the login showcase (in-game trot is 2.4). */
const BREED_WALK_HZ = 1.15;
const BREED_PEEK_FRAME = 0;

let breedSheets: BreedSheetBundle | null = null;
let breedPeekPrevSheets: BreedSheetBundle | null = null;
let breedPeekNextSheets: BreedSheetBundle | null = null;
let breedWalkFrame = 0;
let breedAnimRaf = 0;
let breedAnimLast = 0;
let breedGaitPhase = 0;
let breedLoadGen = 0;

function breedIndexAt(offset: number): number {
  const n = DOG_BREEDS.length;
  return ((selectedBreed + offset) % n + n) % n;
}

function saveSelectedBreed(index: number): void {
  selectedBreed = ((index % DOG_BREEDS.length) + DOG_BREEDS.length) % DOG_BREEDS.length;
  try {
    localStorage.setItem(BREED_STORAGE_KEY, String(selectedBreed));
  } catch {
    // ignore
  }
}

function paintBreedOnto(
  canvas: HTMLCanvasElement,
  breedIndex: number,
  sheets: BreedSheetBundle,
  frame: number,
): void {
  const breed = DOG_BREEDS[breedIndex]!;
  const baked = renderInGameDogCanvas(breed, sheets, frame, "walk");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  ctx.imageSmoothingEnabled = false;
  const scale = Math.min(w / baked.width, h / baked.height) * 0.92;
  const dw = Math.max(1, Math.round(baked.width * scale));
  const dh = Math.max(1, Math.round(baked.height * scale));
  ctx.drawImage(baked, Math.floor((w - dw) / 2), Math.floor((h - dh) / 2), dw, dh);
}

function paintBreedFrame(frame: number): void {
  if (!breedPreview || !breedSheets) {
    return;
  }
  paintBreedOnto(breedPreview, selectedBreed, breedSheets, frame);
}

function paintBreedPeeks(): void {
  if (breedPeekPrev && breedPeekPrevSheets) {
    paintBreedOnto(breedPeekPrev, breedIndexAt(-1), breedPeekPrevSheets, BREED_PEEK_FRAME);
  }
  if (breedPeekNext && breedPeekNextSheets) {
    paintBreedOnto(breedPeekNext, breedIndexAt(1), breedPeekNextSheets, BREED_PEEK_FRAME);
  }
}

function playBreedSelectMotion(): void {
  const focus = breedSlotFocus;
  if (!focus) {
    return;
  }
  focus.classList.remove("is-selecting");
  // Force reflow so re-triggering the animation works on rapid clicks.
  void focus.offsetWidth;
  focus.classList.add("is-selecting");
}

async function showBreed(index: number, animate = true): Promise<void> {
  saveSelectedBreed(index);
  const focusBreed = DOG_BREEDS[selectedBreed]!;
  const prevBreed = DOG_BREEDS[breedIndexAt(-1)]!;
  const nextBreed = DOG_BREEDS[breedIndexAt(1)]!;
  if (breedNameLabel) {
    breedNameLabel.textContent = focusBreed.label;
  }
  if (animate) {
    playBreedSelectMotion();
  }
  const gen = ++breedLoadGen;
  const [focusSheets, prevSheets, nextSheets] = await Promise.all([
    loadBreedSheetBundle(focusBreed),
    loadBreedSheetBundle(prevBreed),
    loadBreedSheetBundle(nextBreed),
  ]);
  if (gen !== breedLoadGen) {
    return;
  }
  breedSheets = focusSheets;
  breedPeekPrevSheets = prevSheets;
  breedPeekNextSheets = nextSheets;
  breedWalkFrame = -1;
  paintBreedFrame(0);
  paintBreedPeeks();
}

function tickBreedWalk(now: number): void {
  breedAnimRaf = requestAnimationFrame(tickBreedWalk);
  if (!breedSheets || !breedPreview) {
    return;
  }
  if (!breedAnimLast) {
    breedAnimLast = now;
  }
  const dt = Math.min(0.05, (now - breedAnimLast) / 1000);
  breedAnimLast = now;
  breedGaitPhase += dt * BREED_WALK_HZ * Math.PI * 2;
  const next =
    Math.floor((breedGaitPhase / (Math.PI * 2)) * DOG_WALK_FRAMES) % DOG_WALK_FRAMES;
  if (next !== breedWalkFrame) {
    breedWalkFrame = next;
    paintBreedFrame(next);
  }
}

function setupBreedCarousel(): void {
  if (!breedCarousel || !breedPreview) {
    return;
  }
  const step = (delta: number) => {
    void showBreed(selectedBreed + delta, true);
  };
  breedPrev?.addEventListener("click", () => step(-1));
  breedNext?.addEventListener("click", () => step(1));
  breedSlotPrev?.addEventListener("click", () => step(-1));
  breedSlotNext?.addEventListener("click", () => step(1));
  breedSlotFocus?.addEventListener("animationend", () => {
    breedSlotFocus.classList.remove("is-selecting");
  });
  breedCarousel.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
    }
  });
  breedCarousel.tabIndex = 0;
  breedCarousel.setAttribute("role", "group");
  breedCarousel.setAttribute("aria-label", "Dog breed");
  void showBreed(selectedBreed, false).then(() => {
    cancelAnimationFrame(breedAnimRaf);
    breedAnimLast = 0;
    breedAnimRaf = requestAnimationFrame(tickBreedWalk);
  });
}

function populateNameSelects(): void {
  const fragment = document.createDocumentFragment();
  for (const name of DOG_NAMES) {
    const option = document.createElement("option");
    option.value = name;
    option.textContent = name;
    fragment.append(option);
  }
  nameInput.replaceChildren(fragment);
}

function setSelectedName(select: HTMLSelectElement, name: string): void {
  const match = DOG_NAMES.find((n) => n.toLowerCase() === name.toLowerCase());
  select.value = match ?? DOG_NAMES[0] ?? "Pupper";
}

function hasAcceptedLegal(): boolean {
  try {
    return localStorage.getItem(LEGAL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markLegalAccepted(): void {
  try {
    localStorage.setItem(LEGAL_STORAGE_KEY, "1");
  } catch {
    // ignore quota / private mode
  }
  if (acceptLegalGuest) {
    acceptLegalGuest.checked = true;
  }
  if (acceptLegalAccount) {
    acceptLegalAccount.checked = true;
  }
}

function requireLegal(checkbox: HTMLInputElement | null): boolean {
  if (hasAcceptedLegal()) {
    return true;
  }
  if (checkbox?.checked) {
    markLegalAccepted();
    return true;
  }
  hud.toast("Please accept the Terms and Privacy Policy to enter.");
  return false;
}

function mountBarkBar(): void {
  if (!barkBar) {
    return;
  }
  barkBar.replaceChildren();
  for (const id of BARK_IDS) {
    const bark = BARKS[id];
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "bark-btn";
    btn.textContent = bark.label;
    btn.title = bark.text;
    btn.addEventListener("click", () => {
      if (!playing) {
        return;
      }
      net.send({ type: "bark", barkId: bark.id });
    });
    barkBar.append(btn);
  }
}

mountBarkBar();
if (hasAcceptedLegal()) {
  if (acceptLegalGuest) {
    acceptLegalGuest.checked = true;
  }
  if (acceptLegalAccount) {
    acceptLegalAccount.checked = true;
  }
}

function loadNameCycleSeen(): string[] {
  try {
    const raw = localStorage.getItem(NAME_CYCLE_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is string => typeof item === "string");
  } catch {
    return [];
  }
}

function saveNameCycleSeen(seen: string[]): void {
  try {
    localStorage.setItem(NAME_CYCLE_STORAGE_KEY, JSON.stringify(seen));
  } catch {
    // Ignore quota / private-mode failures - cycle still works in-memory this session.
  }
}

function fillRandomName(avoid?: string): void {
  const { name, seen } = nextCycledName(loadNameCycleSeen(), avoid);
  saveNameCycleSeen(seen);
  setSelectedName(nameInput, name);
}

function showTab(which: "guest" | "account"): void {
  const guest = which === "guest";
  tabGuest.classList.toggle("is-active", guest);
  tabAccount.classList.toggle("is-active", !guest);
  tabGuest.setAttribute("aria-selected", String(guest));
  tabAccount.setAttribute("aria-selected", String(!guest));
  joinForm.hidden = !guest;
  panelAccount.hidden = guest;
  gateTitle.textContent = guest ? "Choose your dog name" : "Welcome back";
  clearEnterErrors();
  if (!guest) {
    firebaseMissing.hidden = isFirebaseConfigured();
    accountSignedOut.hidden = !isFirebaseConfigured() || Boolean(authUser);
    accountSignedIn.hidden = !authUser;
  }
}

function renderAccount(): void {
  if (!authUser) {
    accountSignedOut.hidden = !isFirebaseConfigured();
    accountSignedIn.hidden = true;
    return;
  }
  accountSignedOut.hidden = true;
  accountSignedIn.hidden = false;
  const dog = accountDogName(authUser, nameInput.value || randomName());
  accountSummary.textContent = `Signed in as ${dog}${authUser.email ? ` · ${authUser.email}` : ""}`;
}

async function enterParkGuest(): Promise<void> {
  if (!requireLegal(acceptLegalGuest)) {
    return;
  }
  if (guestEnter.disabled || guestEnter.hidden) {
    return;
  }
  const name = nameInput.value || randomName();
  setSelectedName(nameInput, name);
  setEnterLoading(true, "guest");
  hud.setStatus("Entering the park…");

  const guestEmail = guestEmailInput.value.trim();
  if (guestEmail && isFirebaseConfigured()) {
    try {
      await sendMagicLink(guestEmail, name);
      hud.toast("Check your email for a link to save your pup.");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Could not send save link.";
      hud.toast(message);
    }
  }

  net.joinPark({ name, mode: "guest", color: selectedBreed });
}

async function enterParkAccount(): Promise<void> {
  if (!requireLegal(acceptLegalAccount)) {
    return;
  }
  if (!authUser) {
    showEnterError("Sign in to save your pup.", "account");
    return;
  }
  if (enterAsAccount.disabled || enterAsAccount.hidden) {
    return;
  }
  setEnterLoading(true, "account");
  const token = await currentIdToken();
  if (!token) {
    showEnterError("Could not get your account token. Try again.", "account");
    return;
  }
  const name = accountDogName(authUser, nameInput.value || randomName());
  hud.setStatus("Entering the park…");
  net.joinPark({ name, mode: "account", idToken: token });
}

function clearEnterErrors(): void {
  guestEnterError.hidden = true;
  guestEnterError.textContent = "";
  accountEnterError.hidden = true;
  accountEnterError.textContent = "";
}

function setEnterLoading(on: boolean, which: "guest" | "account" | "both" = "both"): void {
  if (on) {
    clearEnterErrors();
  }
  const guest = which === "guest" || which === "both";
  const account = which === "account" || which === "both";
  if (guest) {
    guestEnter.hidden = on;
    guestEnter.disabled = on;
    guestEnterBusy.hidden = !on;
    guestEnter.parentElement?.classList.toggle("is-busy", on);
  }
  if (account) {
    enterAsAccount.hidden = on;
    enterAsAccount.disabled = on;
    accountEnterBusy.hidden = !on;
    enterAsAccount.parentElement?.classList.toggle("is-busy", on);
  }
}

function showEnterError(message: string, which: "guest" | "account" | "both" = "both"): void {
  setEnterLoading(false, which);
  if (which === "guest" || which === "both") {
    guestEnterError.textContent = message;
    guestEnterError.hidden = false;
  }
  if (which === "account" || which === "both") {
    accountEnterError.textContent = message;
    accountEnterError.hidden = false;
  }
  hud.setStatus(message);
}

joinForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void enterParkGuest();
});

shuffleName.addEventListener("click", () => {
  fillRandomName(nameInput.value);
  nameInput.focus();
});

tabGuest.addEventListener("click", () => showTab("guest"));
tabAccount.addEventListener("click", () => showTab("account"));

googleSignIn.addEventListener("click", () => {
  void signInWithGoogle()
    .then(() => {
      hud.toast("Welcome back. Your pup is ready.");
      showTab("account");
    })
    .catch((error: unknown) => {
      const message = error instanceof Error ? error.message : "Google sign-in failed.";
      hud.toast(message);
    });
});

magicLinkForm.addEventListener("submit", (event) => {
  event.preventDefault();
  const email = loginEmailInput.value.trim();
  sendMagicLinkBtn.disabled = true;
  void sendMagicLink(email)
    .then(() => {
      magicLinkSent.hidden = false;
      magicLinkForm.hidden = true;
      hud.toast("Magic link sent. Check your email.");
    })
    .catch((error: unknown) => {
      const message = error instanceof Error ? error.message : "Could not send link.";
      hud.toast(message);
    })
    .finally(() => {
      sendMagicLinkBtn.disabled = false;
    });
});

enterAsAccount.addEventListener("click", () => {
  void enterParkAccount();
});

signOutBtn.addEventListener("click", () => {
  void signOutAccount().then(() => {
    hud.toast("Signed out. You can still get started as a guest.");
    showTab("guest");
  });
});

net.on((message) => {
  if (message.type === "spectatorWelcome") {
    if (!parkView) {
      bootPreview(message);
    } else {
      parkView.applySpectator(message);
    }
    hud.setStatus(
      message.players.length > 0
        ? `Watching the park · ${message.players.length} dog${message.players.length === 1 ? "" : "s"} out`
        : "Watching the park",
    );
    return;
  }
  if (message.type === "welcome") {
    beginPlaying(message);
    hud.handleServer(message);
    return;
  }
  hud.handleServer(message);
  if (message.type === "error") {
    if (!playing) {
      const offline =
        message.message.includes("unreachable") || message.message.includes("Disconnected");
      const which = joinForm.hidden ? "account" : "guest";
      showEnterError(
        offline ? "Park offline - try again in a moment." : message.message,
        which,
      );
      return;
    }
    hud.setStatus(message.message);
    hud.toast(message.message);
  }
});

function bootPreview(
  spectator?: Extract<ServerMessage, { type: "spectatorWelcome" }>,
): void {
  if (parkView) {
    return;
  }
  document.body.classList.add("at-gate");
  const root = document.getElementById("game-root");
  if (!root) {
    return;
  }
  parkView = new ParkView(root, { net, hud, spectator });
}

function ensurePreviewGame(): void {
  if (!parkView) {
    bootPreview();
  }
}

function beginPlaying(welcome: Extract<ServerMessage, { type: "welcome" }>): void {
  playing = true;
  setEnterLoading(false);
  clearEnterErrors();
  document.body.classList.remove("at-gate", "is-booting");
  document.body.classList.add("is-playing");
  dismissGate();
  hud.show();
  hud.setStatus(`In the park as ${welcome.you.name}`);
  hud.render(welcome.you);

  const root = document.getElementById("game-root");
  if (!root) {
    return;
  }

  if (!parkView) {
    parkView = new ParkView(root, { net, hud, welcome });
    return;
  }

  parkView.resize();
  parkView.enterPlay(welcome);
}

/** Soft fade so the login card doesn't pop off. */
function dismissGate(): void {
  if (gate.hidden || gate.classList.contains("is-leaving")) {
    gate.hidden = true;
    gate.classList.remove("is-leaving", "is-visible");
    return;
  }
  const finish = (): void => {
    gate.hidden = true;
    gate.classList.remove("is-leaving", "is-visible");
    gate.removeEventListener("transitionend", onEnd);
  };
  const onEnd = (event: TransitionEvent): void => {
    if (event.target !== gate || event.propertyName !== "opacity") {
      return;
    }
    finish();
  };
  gate.classList.add("is-leaving");
  gate.addEventListener("transitionend", onEnd);
  // Fallback if reduced-motion / no transition fires.
  window.setTimeout(finish, 560);
}

function revealGate(): void {
  document.body.classList.remove("is-booting");
  // Double rAF so the browser paints opacity:0 before transitioning in.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      gate.classList.add("is-visible");
    });
  });
}

populateNameSelects();
setupBreedCarousel();
fillRandomName();
showTab("guest");
hud.setStatus("Looking into the park…");
nameInput.focus();

// Boot the scenic park immediately, then attach to the live room as a spectator.
ensurePreviewGame();
net.spectate();
revealGate();

void completeMagicLinkSignInIfPresent()
  .then((user) => {
    if (user) {
      hud.toast("You're signed in!");
      showTab("account");
    }
  })
  .catch((error: unknown) => {
    const message = error instanceof Error ? error.message : "Sign-in link failed.";
    hud.toast(message);
  });

watchAuth((user) => {
  authUser = user;
  renderAccount();
  if (user && !panelAccount.hidden) {
    showTab("account");
  }
});

void initAds();
